import { css, html, LitElement, nothing, type PropertyValues } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { when } from 'lit/directives/when.js'
import { componentText, HostAriaLabelMixin, SchmancyFormField } from '@schmancy/mixins/index'
import '@schmancy/primitives/busy/spinner'

export type SchmancySwitchChangeEvent = CustomEvent<{ value: boolean }>

/**
 * Binary on/off control. Form-associated, keyboard-accessible, ARIA
 * `role="switch"`. Distinct from `<schmancy-checkbox>`: a switch is an
 * immediate state change, a checkbox is a selection in a form to submit.
 *
 * Geometry and color follow Material Design 3 — 52×32 track, 16→24→28 thumb
 * deltas across unselected / selected / pressed, and a state layer that lights
 * up around the thumb on hover, focus, and press.
 *
 * @element schmancy-switch
 * @fires change - `CustomEvent<{ value: boolean }>` when the state changes.
 * @csspart track - The background track.
 * @csspart thumb - The moving thumb.
 */
@customElement('schmancy-switch')
export class SchmancySwitch extends HostAriaLabelMixin(
	SchmancyFormField(css`
	:host {
		display: inline-flex;
		--_track-w: 3.25rem;
		--_track-h: 2rem;
		--_thumb-unchecked: 1rem;
		--_thumb-checked: 1.5rem;
		--_thumb-pressed: 1.75rem;
		--_state-layer: 2.5rem;
		--_ease: cubic-bezier(0.2, 0, 0, 1);
		--_dur: 200ms;
	}

	:host([disabled]) {
		pointer-events: none;
	}

	/* Readonly keeps the switch focusable and announced; only the pointer
	   affordance drops, because nothing moves when it is clicked. */
	:host([readonly]) button {
		cursor: default;
	}

	/* Nor does the state layer light up under a hover that cannot move it. */
	:host([readonly]) button:hover .state-layer,
	:host([readonly]) button:active .state-layer {
		opacity: 0;
	}

	button {
		appearance: none;
		background: none;
		border: 0;
		padding: 0;
		margin: 0;
		cursor: pointer;
		font: inherit;
		display: inline-flex;
		align-items: center;
		outline: none;
	}

	.track {
		position: relative;
		display: inline-flex;
		align-items: center;
		width: var(--_track-w);
		height: var(--_track-h);
		border-radius: var(--schmancy-radius-full, calc(infinity * 1px));
		background: var(--schmancy-sys-color-surface-containerHighest, #e6e0e9);
		border: 2px solid var(--schmancy-sys-color-outline, #79747e);
		box-sizing: border-box;
		transition:
			background-color var(--_dur) var(--_ease),
			border-color var(--_dur) var(--_ease);
	}

	/* Off-state hover/focus deepens the track contrast so the affordance reads.
	   Hover and press rules skip a busy switch through :not(:where([aria-busy])),
	   which carries no specificity, so every rule they used to lose to still wins. */
	button:hover:not(:where([aria-busy])) .track {
		background: color-mix(
			in srgb,
			var(--schmancy-sys-color-surface-on, #1c1b1f) 8%,
			var(--schmancy-sys-color-surface-containerHighest, #e6e0e9)
		);
		border-color: var(--schmancy-sys-color-surface-on, #1c1b1f);
	}

	:host(:state(checked)) .track {
		background: var(--schmancy-sys-color-primary-default, #6750a4);
		border-color: var(--schmancy-sys-color-primary-default, #6750a4);
	}

	:host(:state(checked)) button:hover:not(:where([aria-busy])) .track {
		background: color-mix(in srgb, black 6%, var(--schmancy-sys-color-primary-default, #6750a4));
		border-color: color-mix(in srgb, black 6%, var(--schmancy-sys-color-primary-default, #6750a4));
	}

	:host([disabled]) .track {
		background: color-mix(in srgb, var(--schmancy-sys-color-surface-containerHighest, #e6e0e9) 12%, transparent);
		border-color: color-mix(in srgb, var(--schmancy-sys-color-surface-on, #1c1b1f) 12%, transparent);
	}

	:host([disabled]:state(checked)) .track {
		background: color-mix(in srgb, var(--schmancy-sys-color-surface-on, #1c1b1f) 12%, transparent);
		border-color: transparent;
	}

	/* Thumb container — owns the slide animation and centers the state layer + thumb. */
	.thumb-slot {
		position: absolute;
		top: 50%;
		left: 0;
		display: grid;
		place-items: center;
		width: var(--_state-layer);
		height: var(--_state-layer);
		transform: translate(calc(var(--_track-h) / 2 - var(--_state-layer) / 2), -50%);
		transition: transform var(--_dur) var(--_ease);
		pointer-events: none;
	}

	:host(:state(checked)) .thumb-slot {
		transform: translate(calc(var(--_track-w) - var(--_track-h) / 2 - var(--_state-layer) / 2), -50%);
	}

	/* State layer — the M3 hover/focus/pressed halo. */
	.state-layer {
		position: absolute;
		inset: 0;
		border-radius: var(--schmancy-radius-full, calc(infinity * 1px));
		background: var(--schmancy-sys-color-surface-on, #1c1b1f);
		opacity: 0;
		transition: opacity var(--_dur) var(--_ease);
	}

	:host(:state(checked)) .state-layer {
		background: var(--schmancy-sys-color-primary-default, #6750a4);
	}

	button:hover:not(:where([aria-busy])) .state-layer {
		opacity: var(--schmancy-sys-state-opacity-hover, 0.08);
	}

	button:focus-visible .state-layer {
		opacity: var(--schmancy-sys-state-opacity-focus, 0.12);
	}

	button:active:not(:where([aria-busy])) .state-layer,
	:host([data-pressed]) .state-layer {
		opacity: var(--schmancy-sys-state-opacity-pressed, 0.12);
	}

	.thumb {
		position: relative;
		width: var(--_thumb-unchecked);
		height: var(--_thumb-unchecked);
		border-radius: var(--schmancy-radius-full, calc(infinity * 1px));
		background: var(--schmancy-sys-color-outline, #79747e);
		transition:
			width var(--_dur) var(--_ease),
			height var(--_dur) var(--_ease),
			background-color var(--_dur) var(--_ease);
	}

	:host(:state(checked)) .thumb {
		width: var(--_thumb-checked);
		height: var(--_thumb-checked);
		background: var(--schmancy-sys-color-primary-on, #ffffff);
	}

	/* Off-state hover: thumb jumps to the strongest on-surface ink so the
	   contrast against the now-tinted track is unambiguous. */
	button:hover:not(:where([aria-busy])) .thumb {
		background: var(--schmancy-sys-color-surface-on, #1c1b1f);
	}

	button:focus-visible .thumb {
		background: var(--schmancy-sys-color-surface-on, #1c1b1f);
	}

	:host(:state(checked)) button:hover:not(:where([aria-busy])) .thumb,
	:host(:state(checked)) button:focus-visible .thumb {
		background: var(--schmancy-sys-color-primary-container, #eaddff);
	}

	button:active:not(:where([aria-busy])) .thumb,
	:host([data-pressed]) .thumb {
		width: var(--_thumb-pressed);
		height: var(--_thumb-pressed);
	}

	:host([disabled]) .thumb {
		background: color-mix(in srgb, var(--schmancy-sys-color-surface-on, #1c1b1f) 38%, transparent);
	}

	:host([disabled]:state(checked)) .thumb {
		background: var(--schmancy-sys-color-surface-default, #fef7ff);
	}

	/* Error state — error tint on the outline + state layer. */
	:host([data-error]) .track {
		border-color: var(--schmancy-sys-color-error-default, #b3261e);
	}

	:host([data-error]:state(checked)) .track {
		background: var(--schmancy-sys-color-error-default, #b3261e);
		border-color: var(--schmancy-sys-color-error-default, #b3261e);
	}

	:host([data-error]) .state-layer {
		background: var(--schmancy-sys-color-error-default, #b3261e);
	}

	/* Busy — the save this switch started is in flight. Nothing dims: track and
	   thumb keep their live colours, so the spinner is a signal touch and keyboard
	   reach as much as a pointer. The thumb takes its 24px icon size in both
	   positions so a 16px spinner reads; the thumb slot is absolutely placed, so
	   that growth moves no box. The ink is the thumb's own on-colour. */
	:host(:state(busy)) button {
		cursor: progress;
	}

	:host(:state(busy)) .thumb {
		display: grid;
		place-items: center;
		width: var(--_thumb-checked);
		height: var(--_thumb-checked);
		color: var(--schmancy-sys-color-surface-default, #fef7ff);
	}

	:host(:state(busy):state(checked)) .thumb {
		color: var(--schmancy-sys-color-primary-default, #6750a4);
	}

	.hint {
		margin-top: 0.25rem;
		font-size: 0.75rem;
		line-height: 1rem;
		color: var(--schmancy-sys-color-error-default, #b3261e);
	}

	@media (prefers-reduced-motion: reduce) {
		.track,
		.thumb,
		.thumb-slot,
		.state-layer {
			transition: none;
		}
	}
`),
) {
	// Inner <button> is the focusable surface; route host focus to it.
	protected static shadowRootOptions = {
		...LitElement.shadowRootOptions,
		delegatesFocus: true,
	}

	/**
	 * The string written to FormData when the switch is on. Native
	 * `<input type=checkbox value="...">` semantics. Defaults to `'on'` to
	 * match HTML checkbox conventions.
	 */
	@property({ type: String, reflect: true })
	override value: string = 'on'

	/** Boolean on/off state. Native `<input type=checkbox>.checked` semantics. */
	@property({ type: Boolean, reflect: true })
	checked: boolean = false

	/**
	 * Who owns `checked`. Unset, the switch owns it: activation flips its own
	 * state and then says so, which is what a consumer reading `checked` back
	 * off the element wants. Set, the CONSUMER owns it: activation writes
	 * nothing here and only dispatches `change` carrying the state the user
	 * asked for, so the bound value is the one thing that moves the switch.
	 *
	 * Reach for it whenever the bound value is DERIVED rather than stored. A
	 * derivation is free to recompute to the value it already had — a master
	 * switch over "admit everything", which an empty selection satisfies from
	 * either direction, is exactly that shape — and an unchanged binding is one
	 * lit-html skips committing. The self-flipped switch then keeps a state its
	 * binding never agreed to, and every later gesture reads back as the value
	 * the consumer is already showing: a `CONTROLLED_VALUE_ECHO_IS_NOT_INTENT`
	 * guard swallows it as an echo, and the lever is dead for good.
	 */
	@property({ type: Boolean, reflect: true })
	controlled: boolean = false

	/**
	 * The change this switch asked for is being saved. Same contract as
	 * `schmancy-icon-button`'s `busy`, and deliberately not `disabled`:
	 * `aria-busy` says "wait" where `aria-disabled` says "you cannot", and only
	 * `disabled` gives up the tab stop. While busy a click, Space or Enter moves
	 * nothing and dispatches nothing, focus stays where it is, and a spinner sits
	 * in the thumb. A switch that is also `disabled` is disabled: no spinner and
	 * no `aria-busy`.
	 */
	@property({ type: Boolean, reflect: true })
	busy: boolean = false

	constructor() {
		super()
		// Constructor-registered, so it precedes a consumer's own `@click` on this
		// host and `stopImmediatePropagation` reaches it, as on schmancy-icon-button.
		this.addEventListener('click', (e: MouseEvent) => {
			if (!this.busy) return
			e.preventDefault()
			e.stopImmediatePropagation()
		})
	}

	/** Snapshot of `checked` at first render — drives the `dirty` override. */
	private checkedDefault: boolean = false

	override firstUpdated(changed: PropertyValues): void {
		super.firstUpdated(changed)
		this.checkedDefault = this.checked
	}

	override get dirty(): boolean {
		return this.checked !== this.checkedDefault
	}

	override willUpdate(changed: PropertyValues): void {
		super.willUpdate(changed)
		if (changed.has('checked') || changed.has('value') || changed.has('name')) {
			this.internals?.setFormValue(this.checked ? this.value : null)
			if (this.checked) this.internals?.states.add('checked')
			else this.internals?.states.delete('checked')
			this.checkValidity()
		}
		if (changed.has('required') || changed.has('disabled')) {
			this.checkValidity()
		}
		if (changed.has('error')) {
			this.toggleAttribute('data-error', this.error)
		}
		if (changed.has('busy') || changed.has('disabled')) {
			if (this.busy && !this.disabled) {
				this.setAttribute('aria-busy', 'true')
				this.internals?.states.add('busy')
			} else {
				this.removeAttribute('aria-busy')
				this.internals?.states.delete('busy')
			}
		}
	}

	override checkValidity(): boolean {
		if (this.required && !this.checked) {
			return this.setFieldValidity(
				{ valueMissing: true },
				this.errorMessages?.valueMissing ?? componentText('switch.error.valueMissing'),
			)
		}
		return this.setFieldValidity({})
	}

	override toFormEntries(): Array<[string, FormDataEntryValue]> {
		if (!this.name || this.disabled || !this.checked) return []
		return [[this.name, this.value]]
	}

	override resetForm(): void {
		this.checked = this.checkedDefault
		super.resetForm()
	}

	private toggle = () => {
		if (this.disabled || this.readonly || this.busy) return
		const requested = !this.checked
		if (!this.controlled) this.checked = requested
		this.markTouched()
		this.emitChange({ value: requested })
	}

	private onKeydown = (e: KeyboardEvent) => {
		if (this.disabled || this.readonly || this.busy) return
		if (e.key === ' ' || e.key === 'Enter') {
			e.preventDefault()
			this.toggleAttribute('data-pressed', true)
			this.toggle()
		}
	}

	private onKeyup = (e: KeyboardEvent) => {
		if (e.key === ' ' || e.key === 'Enter') {
			this.toggleAttribute('data-pressed', false)
		}
	}

	render() {
		return html`
			<button
				type="button"
				role="switch"
				aria-checked=${this.checked ? 'true' : 'false'}
				aria-label=${this.ariaLabel || this.label || nothing}
				aria-required=${this.required ? 'true' : 'false'}
				aria-readonly=${this.readonly ? 'true' : 'false'}
				aria-disabled=${this.disabled || this.readonly ? 'true' : 'false'}
				aria-invalid=${this.error ? 'true' : 'false'}
				aria-busy=${ifDefined(this.busy && !this.disabled ? 'true' : undefined)}
				?disabled=${this.disabled}
				@click=${this.toggle}
				@keydown=${this.onKeydown}
				@keyup=${this.onKeyup}
			>
				<span part="track" class=${this.classMap({ track: true })}>
					<span class="thumb-slot">
						<span class="state-layer" aria-hidden="true"></span>
						<span part="thumb" class="thumb"
							>${when(
								this.busy && !this.disabled,
								() => html`<schmancy-spinner aria-hidden="true" size="xs"></schmancy-spinner>`,
							)}</span
						>
					</span>
				</span>
			</button>
			${
				this.error && this.validationMessage
					? html`<div role="alert" class="hint">${this.validationMessage}</div>`
					: nothing
			}
		`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-switch': SchmancySwitch
	}
}
